import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule } from 'ng-apexcharts';
import { GridService } from '../../services/grid.service';
import { marked } from 'marked';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-databricks-report',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  templateUrl: './databricks-report.component.html',
  styleUrls: ['./databricks-report.component.scss']
})
export class DatabricksReportComponent implements OnInit {

  savedAnalyses: any[] = [];
  sharedAnalyses: any[] = [];
  activeTab: 'saved' | 'shared' = 'saved';

  selectedAnalysisId: string | null = null;
  selectedReport: any = null;

  isLoadingList = true;
  isLoadingReport = false;
  user: any;
  configId: string = '';

  constructor(
    private gridService: GridService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.user = this.getCurrentUser();
    const routeId = this.route.snapshot.paramMap.get('id');

    if (routeId) {
      this.gridService.getScopeData('DBCONFIG', 'READ', { ViewCode: 'DBCONFIG.DEFAULT', Filter: `{ AliasCode: "${routeId}" }` })
        .subscribe({
          next: (res: any) => {
            if (res && res.data && res.data.length > 0) {
              const dbConfig = res.data[0];
              this.configId = dbConfig._id;
              if (this.user) {
                this.loadAnalyses();
              } else {
                this.isLoadingList = false;
              }
            } else {
              console.error(`Configurazione non trovata per AliasCode: ${routeId}`);
              this.isLoadingList = false;
            }
          },
          error: (err) => {
            console.error('Errore durante la lettura della configurazione DBCONFIG', err);
            this.isLoadingList = false;
          }
        });
    } else {
      this.configId = (environment as any).databricks?.id || '';
      if (this.user) {
        this.loadAnalyses();
      } else {
        this.isLoadingList = false;
      }
    }
  }

  private getCurrentUser(): any {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (e) {
      console.warn('Errore lettura user da localStorage', e);
    }
    return null;
  }

  loadAnalyses(): void {
    const userId = this.user?.id || this.user?._id;
    if (!userId) {
      this.isLoadingList = false;
      return;
    }

    this.isLoadingList = true;

    // Load saved
    const filterSaved = this.configId ? `{ CreationUserCode: "${userId}", ConfigCode: "${this.configId}" }` : `{ CreationUserCode: "${userId}" }`;
    this.gridService.getScopeData('DBAIDATA', 'READ', { ViewCode: 'DBAIDATA.DEFAULT', Filter: filterSaved, Sort: '{ LastEditDate: -1 }' }).subscribe((res: any) => {
      this.isLoadingList = false;
      if (res && res.data) {
        this.savedAnalyses = res.data;
      }
    });

    // Load shared
    const filterShared = this.configId ? `{ $or: [{ RecipientUserCode: "${userId}"}, { CreationUserCode: "${userId}" }], ConfigCode: "${this.configId}" }` : `{ $or: [{ RecipientUserCode: "${userId}"}, { CreationUserCode: "${userId}" }] }`;
    this.gridService.getScopeData('DBAIDATASHARE', 'READ', {
      ViewCode: 'DBAIDATASHARE.DEFAULT',
      Filter: filterShared
    }).subscribe((res: any) => {
      if (res && res.data) {
        const groupedMap = new Map<string, any>();
        res.data.forEach((item: any) => {
          const analysisIdValue = item.AnalysisId?.value || item.AnalysisId || item._id || item.Id || item.id;
          if (!groupedMap.has(analysisIdValue)) {
            groupedMap.set(analysisIdValue, {
              ...item,
              SentTo: [],
              ReceivedFrom: []
            });
          }
          const groupedItem = groupedMap.get(analysisIdValue);
          const isSender = item.CreationUserCode?.value === userId;
          const isRecipient = item.RecipientUserCode?.value === userId;
          if (isSender && item.RecipientUserCode) {
            const name = `${item.RecipientUserCode.attribute1 || ''} ${item.RecipientUserCode.attribute2 || ''}`.trim() || item.RecipientUserCode.text || 'Utente';
            if (!groupedItem.SentTo.includes(name)) groupedItem.SentTo.push(name);
          } else if (isRecipient && item.CreationUserCode) {
            const name = `${item.CreationUserCode.attribute1 || ''} ${item.CreationUserCode.attribute2 || ''}`.trim() || item.CreationUserCode.text || 'Utente';
            if (!groupedItem.ReceivedFrom.includes(name)) groupedItem.ReceivedFrom.push(name);
          }
        });
        this.sharedAnalyses = Array.from(groupedMap.values());
      }
    });
  }

  getAnalysisId(item: any): string {
    return item.AnalysisId?.value || item.AnalysisId || item._id || item.Id || item.id;
  }

  loadAnalysis(item: any): void {
    const analysisId = this.getAnalysisId(item);
    if (!analysisId) return;

    this.selectedAnalysisId = analysisId;
    this.isLoadingReport = true;
    this.selectedReport = null;

    this.gridService.getScopeData('DBAIDATA', 'READ', { ViewCode: 'DBAIDATA.FULL', Filter: `{ _id: "${analysisId}" }` }).subscribe((res: any) => {
      this.isLoadingReport = false;
      if (res && res.data && res.data.length > 0) {
        const data = res.data[0];

        // Costruisci le options del chart se presente
        const storedChartData = this.normalizeChartData(data.ChartData);
        let loadedChartOptions: any = null;
        if (storedChartData && Array.isArray(storedChartData.rows)) {
          loadedChartOptions = this.buildChartOptions(storedChartData);
        } else if (storedChartData?.series && storedChartData?.chart) {
          loadedChartOptions = storedChartData;
        }

        if (loadedChartOptions && loadedChartOptions.yaxis && loadedChartOptions.yaxis.labels) {
          loadedChartOptions.yaxis.decimalsInFloat = 0;
          loadedChartOptions.yaxis.labels.formatter = (value: number) => this.formatChartValue(value);
        }

        data.ChartData = loadedChartOptions;

        // Parsa table
        data.TableData = this.normalizeTableData(data.TableData);
        if (data.TableData && Array.isArray(data.TableData.rows)) {
          data.TableData.paginatedRows = data.TableData.rows; // Show all or paginated based on how you want it
        }

        if (data.Content) {
          data.Content = marked.parse(data.Content) as string;
        } else if (data.AnswerText) {
          data.Content = marked.parse(data.AnswerText) as string;
        }

        this.selectedReport = data;
      }
    });
  }

  private normalizeChartData(data: any): any {
    if (!data) return null;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        return null;
      }
    }
    return data;
  }

  private normalizeTableData(data: any): any {
    if (!data) return null;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        return null;
      }
    }
    return data;
  }

  private buildChartOptions(chartData: any): any {
    const columns = chartData.columns || [];
    const rows = chartData.rows || [];

    // Trova le colonne categoriche (X) e numeriche (Y)
    const numericIndices: number[] = [];
    const categoryIndices: number[] = [];

    columns.forEach((col: any, index: number) => {
      const typeName = col.type_name?.toUpperCase() || '';
      if (['DOUBLE', 'FLOAT', 'DECIMAL', 'LONG', 'INT', 'BIGINT', 'INTEGER', 'SHORT', 'TINYINT'].includes(typeName)) {
        numericIndices.push(index);
      } else {
        categoryIndices.push(index);
      }
    });

    // Fallback se mancano tipologie chiare
    if (categoryIndices.length === 0 && columns.length > 0) categoryIndices.push(0);
    if (numericIndices.length === 0 && columns.length > 1) numericIndices.push(columns.length - 1);

    // Costruiamo le etichette per l'asse X unendo le colonne categoriche
    const categories = rows.map((row: any[]) => {
      return categoryIndices.map(i => {
        let val = row[i];
        if (val === null || val === undefined) return 'N/D';
        if (columns[i]?.type_name?.toUpperCase() === 'TIMESTAMP' || columns[i]?.type_name?.toUpperCase() === 'DATE') {
          const d = new Date(val);
          if (!isNaN(d.getTime())) return d.toLocaleDateString('it-IT');
        }
        if (typeof val === 'object') {
          try {
            val = JSON.stringify(val);
          } catch {
            val = '[Oggetto]';
          }
        }
        const strVal = String(val);
        return strVal.length > 50 ? strVal.substring(0, 50) + '...' : strVal;
      }).join(' - ');
    });

    // Costruiamo una serie per ogni colonna numerica trovata
    const series = numericIndices.map(i => {
      return {
        name: columns[i]?.name || `Serie ${i}`,
        data: rows.map((row: any[]) => {
          const val = row[i];
          if (val === null || val === undefined) return null;
          const num = Number(val);
          return isNaN(num) ? null : num;
        })
      };
    });

    const customColor = getComputedStyle(document.documentElement).getPropertyValue('--custom-color').trim() || '#2563eb';
    const colors = [customColor, '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

    return {
      series: series,
      chart: { type: chartData.type || 'bar', height: 400, toolbar: { show: true } },
      plotOptions: { bar: { horizontal: false, borderRadius: 2, columnWidth: '60%' } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ['transparent'] },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          trim: true,
          maxHeight: 120,
          style: { fontSize: '11px' }
        }
      },
      yaxis: {
        decimalsInFloat: 0,
        labels: {
          formatter: (value: number) => this.formatChartValue(value)
        }
      },
      tooltip: {
        y: {
          formatter: (value: number) => this.formatChartValue(value)
        }
      },
      legend: { position: 'top', horizontalAlign: 'center' },
      colors: colors
    };
  }

  private formatChartValue(value: number): string {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('it-IT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  parseDate(dateStr: any): Date | string {
    if (!dateStr) return '';
    let d = new Date(dateStr.replace('T', ' ').replace('Z', ''));
    return d;
  }
}
